import { emailSchema, phoneNumberSchema } from 'pages/Complaint/InsuredDetails/schema';
import * as yup from 'yup';
import countries from 'resources/addresses';
import { YesNoList, titleList } from 'contexts/FormProvider';

import { textAreaRegex, validatePostCode, validateTextSchema } from 'utils/commonSchema';
import { isEmpty } from 'lodash';

yup.setLocale({
  mixed: {
    oneOf: '',
    required: '',
  },
});

const textFieldMin = 1;
const languageMin = 3;
const textFieldMax = 50;
const dependentFieldMax = 100;
const addressMax = 225;
const stateMax = 20;

const textFieldNameMin = 1;

const postalAddressErrorMsgMin = 'Remember to include your unit number as well as your street number';

export const dependentNameSchema = yup
  .string()
  .min(textFieldNameMin)
  .max(dependentFieldMax)
  .trim()
  .matches(textAreaRegex)
  .required();

const ageErrorMessage = 'only numeric values are allowed';

export const dependentAgeSchema = yup
  .string()
  .matches(/^[\d]{1,3}$/, ageErrorMessage)
  .required(ageErrorMessage);

export const dependentSchema = yup.array().of(
  yup.object().shape(
    {
      dependentName: yup.string().when('dependentAge', {
        is: (dependentAge: string) => !isEmpty(dependentAge),
        then: () => dependentNameSchema,
      }),
      dependentAge: yup.string().when('dependentName', {
        is: (dependentName: string) => !isEmpty(dependentName),
        then: () => dependentAgeSchema,
        otherwise: () => yup.string().nullable(),
      }),
    },
    [
      ['dependentName', 'dependentAge'],
      ['dependentAge', 'dependentName'],
    ]
  )
);

export const personalDetailsSchemaObject = {
  title: yup
    .string()
    .nullable()

    .oneOf([...titleList, ''])
    .optional(),

  insuredFirstName: validateTextSchema({ minLen: textFieldMin, maxLen: textFieldMax, required: true }),
  insuredLastName: validateTextSchema({ minLen: textFieldMin, maxLen: textFieldMax, required: true }),

  isLanguageInterpreter: yup
    .string()
    .nullable()
    .oneOf([...YesNoList])
    .required(),

  language: yup
    .string()
    .nullable()
    .when('isLanguageInterpreter', {
      is: 'yes',
      then: () =>
        yup
          .string()
          .oneOf([
            'Arabic',
            'Cantonese',
            'Greek',
            'Hindi',
            'Italian',
            'Mandarin',
            'Punjabi',
            'Spanish',
            'Vietnamese',
            'Other',
          ])
          .required(),
    }),

  otherLanguage: yup
    .string()
    .nullable()
    .when(['isLanguageInterpreter', 'language'], {
      is: (isLanguageInterpreter: string, language: string) => isLanguageInterpreter === 'yes' && language === 'Other',
      then: () => validateTextSchema({ minLen: languageMin, maxLen: textFieldMax, required: true }),
    }),

  dependents: dependentSchema,
};

export const contactDetailSchemaObject = {
  cEmailAddress: yup.string().when('iContactPreferedMethod', {
    is: 'email',
    then: () => emailSchema({ excludeEmptyString: false }),
    otherwise: () => emailSchema({ excludeEmptyString: true, required: false }),
  }),

  iContactPreferedMethod: yup.string().oneOf(['email', 'post', 'phone']).required(),

  addressType: yup
    .string()
    .nullable()
    .when('iContactPreferedMethod', {
      is: 'post',
      then: () => yup.string().oneOf(['yes', 'no']).required(),
    }),

  haveOneValidNumber: yup.boolean().when('iContactPreferedMethod', {
    is: 'phone',
    then: () => yup.boolean().oneOf([true]),
  }),

  mPhoneNumber: phoneNumberSchema(),

  hPhoneNumber: phoneNumberSchema(),

  oPhoneNumber: phoneNumberSchema(),

  residentialAddress: yup
    .string()
    .nullable()
    .when(['isManualAddressRes'], {
      is: (isManualAddressRes: boolean) => !isManualAddressRes,
      then: () => yup.string().required(postalAddressErrorMsgMin).min(4, postalAddressErrorMsgMin),
    }),

  isResAddressValid: yup.boolean().when(['isManualAddressRes'], {
    is: (isManualAddressRes: boolean) => !isManualAddressRes,
    then: () => yup.boolean().required().oneOf([true]),
  }),

  rCountry: yup
    .string()
    .nullable()
    .when('isManualAddressRes', {
      is: true,
      then: () => yup.string().oneOf(countries, 'Country is a required field').required().label('Country'),
    }),

  rState: yup.string().when('isManualAddressRes', {
    is: true,
    then: () => validateTextSchema({ minLen: textFieldMin, maxLen: stateMax, required: true }),
  }),

  rStreetAddress: yup.string().when('isManualAddressRes', {
    is: true,
    then: () => validateTextSchema({ minLen: textFieldMin, maxLen: addressMax, required: true }),
  }),

  rSuburb: yup.string().when('isManualAddressRes', {
    is: true,
    then: () => validateTextSchema({ minLen: textFieldMin, maxLen: addressMax, required: true }),
  }),

  rPostcode: yup.string().test('postalCode', 'Invalid postcode', function (postCode = '') {
    const { rState, isManualAddressRes, rCountry } = this.parent;
    if (isManualAddressRes && typeof postCode === 'string') return validatePostCode(rCountry)(rState)(postCode);
    return true;
  }),

  cPostalAddress: yup
    .string()
    .nullable()
    .when(['isManualAddressPhy', 'addressType'], {
      is: (isManualAddressPhy: boolean, addressType: string) => !isManualAddressPhy && addressType === 'no',
      then: () => yup.string().required(postalAddressErrorMsgMin).min(4, postalAddressErrorMsgMin),
    }),

  isPhyAddressValid: yup.boolean().when(['isManualAddressPhy', 'addressType'], {
    is: (isManualAddressPhy: boolean, addressType: string) => !isManualAddressPhy && addressType === 'no',
    then: () => yup.boolean().required().oneOf([true]),
  }),

  pStreetAddress: yup.string().when(['isManualAddressPhy', 'addressType'], {
    is: (isManualAddressPhy: boolean, addressType: string) => addressType === 'no' && isManualAddressPhy,
    then: () => validateTextSchema({ minLen: textFieldMin, maxLen: addressMax, required: true }),
  }),

  pSuburb: yup.string().when(['isManualAddressPhy', 'addressType'], {
    is: (isManualAddressPhy: boolean, addressType: string) => addressType === 'no' && isManualAddressPhy,
    then: () => validateTextSchema({ minLen: textFieldMin, maxLen: addressMax, required: true }),
  }),

  pState: yup.string().when(['isManualAddressPhy', 'addressType'], {
    is: (isManualAddressPhy: boolean, addressType: string) => addressType === 'no' && isManualAddressPhy,
    then: () => validateTextSchema({ minLen: textFieldMin, maxLen: stateMax, required: true }),
  }),
  pCountry: yup
    .string()
    .nullable()
    .when(['isManualAddressPhy', 'addressType'], {
      is: (isManualAddressPhy: boolean, addressType: string) => addressType === 'no' && isManualAddressPhy,
      then: () => yup.string().oneOf(countries, 'Country is a required field').required().label('Country'),
    }),

  pPostcode: yup.string().test('postalCode', 'Invalid postcode', function (postCode) {
    const { isManualAddressPhy, pCountry, pState, addressType } = this.parent;
    if (isManualAddressPhy && addressType === 'no' && typeof postCode === 'string') {
      return validatePostCode(pCountry)(pState)(postCode);
    }
    return true;
  }),
};

export const personalDetailsSchema = yup.object().shape(personalDetailsSchemaObject);
export const contactDetailSchema = yup.object().shape(contactDetailSchemaObject);

const validationSchema = yup.object().shape({
  ...personalDetailsSchemaObject,
  ...contactDetailSchemaObject,
});

export default validationSchema;
